import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, Badge, Button, Select } from '../components/ui';
import { api, ApiError } from '../lib/api';
import type { ApiResponse } from '../lib/types';

interface Rule { rule_id: string; type: string; value: string; campaign_id: string; status: string; hits?: number; last_hit_at?: string; still_filtering?: boolean; reason?: string; created_by?: string }
interface Analysis { campaign_id: string; verdict: string; summary: string; mode: string; window: string; created_at: string; model?: string | null; tokens?: number | null; rules_created?: string[] }

export default function FraudGuard() {
  const [rules, setRules] = useState<Rule[]>([]);
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<'all' | 'global' | 'campaign'>('all');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [names, setNames] = useState<Record<string, string>>({});
  const [dedupeBusy, setDedupeBusy] = useState(false);

  // Conta quantas regras são cópias de outra (mesmo tipo+valor+escopo).
  const duplicateCount = (() => {
    const seen = new Set<string>();
    let dupes = 0;
    for (const r of rules) {
      const key = `${r.type}|${r.value}|${r.campaign_id}`;
      if (seen.has(key)) dupes++;
      else seen.add(key);
    }
    return dupes;
  })();

  async function mergeDuplicates() {
    if (!confirm(`Merge ${duplicateCount} duplicate rule(s)? Blocked counts are summed into the rule that is kept.`)) return;
    setDedupeBusy(true);
    try {
      const r = await api.post<{ groups: number; removed: number }>('/fraud-guard/rules/dedupe', {});
      alert(`Merged ${r.removed} duplicate(s) across ${r.groups} pattern(s).`);
      load();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Failed to merge duplicates');
    } finally {
      setDedupeBusy(false);
    }
  }

  const load = () => {
    setLoading(true);
    api.get<ApiResponse<{ rules: Rule[]; analyses: Analysis[] }> & { rules?: Rule[]; analyses?: Analysis[] }>('/fraud-guard/data')
      .then((r: any) => { setRules(r.rules || []); setAnalyses(r.analyses || []); })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Nome das campanhas para o dropdown — as regras só guardam o id.
  useEffect(() => {
    api.get<ApiResponse<{ id: string; name: string }[]>>('/campaigns/list?withCounts=false')
      .then((r) => {
        const map: Record<string, string> = {};
        for (const c of r.data || []) map[c.id] = c.name;
        setNames(map);
      })
      .catch(() => {});
  }, []);

  // Só as campanhas que de fato têm regra, para o filtro não virar uma lista
  // de 105 itens em que quase nenhum devolve resultado.
  const campaignsWithRules = [...new Set(rules.filter((r) => r.campaign_id !== '*').map((r) => r.campaign_id))]
    .sort((a, b) => (names[a] || a).localeCompare(names[b] || b));

  const visibleRules = rules.filter((r) => {
    const isGlobal = r.campaign_id === '*';
    if (scope === 'global' && !isGlobal) return false;
    if (scope === 'campaign' && isGlobal) return false;
    if (campaignFilter && r.campaign_id !== campaignFilter) return false;
    return true;
  });

  async function toggle(rule_id: string, on: boolean) {
    await api.post('/fraud-guard/rules/status', { rule_id, status: on ? 'on' : 'off' });
    load();
  }
  async function del(rule_id: string) {
    if (!confirm('Delete rule? (false positive)')) return;
    await api.del(`/fraud-guard/rules/delete?rule_id=${encodeURIComponent(rule_id)}`);
    load();
  }

  const active = rules.filter((r) => r.status !== 'off').length;
  const blocked = rules.reduce((a, r) => a + (r.hits || 0), 0);
  const filtering = rules.filter((r) => r.still_filtering).length;

  return (
    <Layout title="Fraud Guard">
      {error && <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">{error}</div>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-5"><div className="text-2xl font-bold text-ink-900">{active}</div><div className="text-sm text-slate-500">Active rules</div></Card>
        <Card className="p-5"><div className="text-2xl font-bold text-rose-600">{blocked.toLocaleString()}</div><div className="text-sm text-slate-500">Blocked clicks</div></Card>
        <Card className="p-5"><div className="text-2xl font-bold text-emerald-600">{filtering}</div><div className="text-sm text-slate-500">Filtering now</div></Card>
        <Card className="p-5"><div className="text-2xl font-bold text-ink-900">{analyses.length}</div><div className="text-sm text-slate-500">Analyses</div></Card>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-2">
        <h2 className="text-sm font-semibold text-ink-900">Active rules</h2>
        {duplicateCount > 0 && (
          <Button className="bg-amber-500 hover:bg-amber-600" onClick={mergeDuplicates} disabled={dedupeBusy}>
            {dedupeBusy ? 'Merging…' : `⇅ Merge ${duplicateCount} duplicate${duplicateCount === 1 ? '' : 's'}`}
          </Button>
        )}
        <div className="flex gap-1">
          {(['all', 'global', 'campaign'] as const).map((v) => (
            <button
              key={v}
              onClick={() => { setScope(v); if (v === 'global') setCampaignFilter(''); }}
              className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                scope === v ? 'bg-chrome-800 text-white' : 'bg-surface-muted text-slate-600 hover:bg-slate-200'
              }`}
            >
              {v}
            </button>
          ))}
        </div>
        {/* Filtrar por campanha não faz sentido com escopo global selecionado. */}
        {scope !== 'global' && campaignsWithRules.length > 0 && (
          <Select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} className="w-72">
            <option value="">All campaigns</option>
            {campaignsWithRules.map((cid) => (
              <option key={cid} value={cid}>{names[cid] || cid}</option>
            ))}
          </Select>
        )}
        <span className="text-xs text-slate-500 ml-auto">
          {visibleRules.length} of {rules.length} rule{rules.length === 1 ? '' : 's'}
        </span>
      </div>
      <Card className="overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-surface-muted">
                <th className="px-4 py-3 font-medium">On</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Value</th>
                <th className="px-4 py-3 font-medium">Scope</th>
                <th className="px-4 py-3 font-medium text-right">Hits</th>
                <th className="px-4 py-3 font-medium">Filtering?</th>
                <th className="px-4 py-3 font-medium">Reason</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && <tr><td colSpan={8} className="px-4 py-6 text-slate-400">Loading…</td></tr>}
              {visibleRules.map((r) => (
                <tr key={r.rule_id} className={r.status === 'off' ? 'opacity-50' : ''}>
                  <td className="px-4 py-3"><input type="checkbox" checked={r.status !== 'off'} onChange={(e) => toggle(r.rule_id, e.target.checked)} /></td>
                  <td className="px-4 py-3"><code className="text-xs">{r.type}</code></td>
                  <td className="px-4 py-3"><code className="text-xs">{r.value}</code></td>
                  <td className="px-4 py-3"><Badge tone={r.campaign_id === '*' ? 'brand' : 'blue'}>{r.campaign_id === '*' ? 'global' : 'campaign'}</Badge></td>
                  <td className="px-4 py-3 text-right font-medium">{(r.hits || 0).toLocaleString()}</td>
                  <td className="px-4 py-3">{r.still_filtering ? <Badge tone="green">yes</Badge> : <Badge tone="neutral">idle</Badge>}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 max-w-xs truncate">{r.reason}</td>
                  <td className="px-4 py-3 text-right"><Button variant="danger" onClick={() => del(r.rule_id)}>×</Button></td>
                </tr>
              ))}
              {!loading && visibleRules.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No rules.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <h2 className="text-sm font-semibold text-ink-900 mb-2">Analyses</h2>
      <div className="space-y-3">
        {analyses.map((a, i) => (
          <Card key={i} className="p-4">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Badge tone={a.verdict === 'fraud' ? 'red' : a.verdict === 'suspicious' ? 'amber' : 'green'}>{a.verdict?.toUpperCase()}</Badge>
                <span className="font-mono text-xs text-slate-500">{a.campaign_id}</span>
                <span className="text-xs text-slate-400">{a.mode} · {a.window} · {new Date(a.created_at).toLocaleString()}</span>
              </span>
              <span className="text-xs text-slate-400">{a.model ? `${a.model} · ${a.tokens ?? 0} tok` : '0 tok (skipped)'}</span>
            </div>
            <p className="text-sm text-slate-600 mt-2">{a.summary}</p>
            {(a.rules_created?.length ?? 0) > 0 && <Badge tone="blue">{a.rules_created!.length} rule{a.rules_created!.length === 1 ? '' : 's'} created</Badge>}
          </Card>
        ))}
        {!loading && analyses.length === 0 && <Card className="p-8 text-center text-slate-400">No analyses yet.</Card>}
      </div>
    </Layout>
  );
}
